import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from 'firebase/firebase';
import { canGetOrganizationDocument, getDocData, isPackedClaims } from 'packages/utils';

import {
  errorNotification,
  organizationFetch,
  userOrgUserFetchError,
  userOrgUserFetchSuccess,
  userOrgUserReset,
} from 'appRedux/actions';
import { ORGANIZATION_SELECT } from 'constants/ActionTypes';
import { FSCollections } from 'constants/database';

const getOrgId = state => state.organizations.organization.id;
const getClaims = state => state.user.access.data?.claims;

const userOrgUserFetchRequest = async (orgId, userId) => {
  const docRef = doc(db, FSCollections.ORGANIZATIONS, orgId, FSCollections.USERS, userId);
  const docSnap = await getDoc(docRef);
  return getDocData(docSnap);
};

const refreshAuthToken = async () => {
  if (auth.currentUser) {
    await auth.currentUser.getIdToken(true);
  }
};

export function* organizationChange() {
  try {
    const orgId = yield select(getOrgId);
    const userId = auth.currentUser?.uid;

    // Packed tokens (pk: 1) fall back to this document. Clear it before the next
    // read so the previous organization's role is not shown for the new one.
    // Reset leaves orgUser.loaded === false until the fetch succeeds or fails.
    // Callers that only read data.access see undefined in that window; loaded
    // false means the read is pending, not that the user has no role.
    yield put(userOrgUserReset());

    if (!orgId || !userId) {
      return;
    }

    const response = yield call(userOrgUserFetchRequest, orgId, userId);
    yield put(userOrgUserFetchSuccess(response));

    const claims = yield select(getClaims);
    const claimsOrgAccess = claims?.org?.[orgId];
    const docOrgAccess = response?.access;

    // When the org user doc and JWT disagree, refresh the token so callables see the same
    // role the UI resolves from the org user document.
    // A packed token drops the org map on purpose, so the entry stays missing no matter
    // how often we refresh. getIdToken(true) would only fire onIdTokenChanged, which
    // fetches access again and selects the organization once more.
    if (
      docOrgAccess &&
      !isPackedClaims(claims) &&
      (claimsOrgAccess === undefined || claimsOrgAccess !== docOrgAccess)
    ) {
      yield call(refreshAuthToken);
    }

    // organizationFetch is still packed-gated: canGetOrganizationDocument →
    // resolveOrgAccess, which falls back to the org-user doc only when pk: 1.
    if (
      claimsOrgAccess === undefined &&
      canGetOrganizationDocument(claims, orgId, docOrgAccess)
    ) {
      yield put(organizationFetch(orgId));
    }
  } catch (error) {
    yield put(userOrgUserFetchError(error));
    yield put(errorNotification(error.toString()));
  }
}

export function* watchOrganizationChange() {
  yield takeEvery(ORGANIZATION_SELECT, organizationChange);
}

export default function* rootSaga() {
  yield all([fork(watchOrganizationChange)]);
}

